import {
  useFacilitiesList,
  useNetworkOptions,
  useSystemOptions,
} from "@features/ax/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { X } from "lucide-react";
import React, { useEffect, useMemo } from "react";

interface FilterPanelProps {
  filters: {
    facility: string;
    system: string;
    network: string;
  };
  onFilterChange: (filterName: string, value: string) => void;
  onClearFilters: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
}) => {
  // Check if any filters are active (not 'all' or empty)
  const hasActiveFilters = Object.values(filters).some(
    (value) => value && value !== "all" && value !== ""
  );

  const { data: facilityList } = useFacilitiesList();
  const { data: systemList } = useSystemOptions();
  const { data: networkList } = useNetworkOptions();

  // Facility options with "All" option
  const facilityOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Facilities" }];

    if (facilityList) {
      const facilityOpts = facilityList.map((facility: any) => ({
        value: facility.id.toString(),
        label: facility.facilityName,
      }));
      options.push(...facilityOpts);
    }

    return options;
  }, [facilityList]);

  const systemOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Systems" }];

    if (systemList) {
      const systemsOpts = systemList?.result.map((system: any) => ({
        value: system.id.toString(),
        label: system.systemName,
      }));
      options.push(...systemsOpts);
    }

    return options;
  }, [systemList]);

  const networkOptions = useMemo(() => {
    const options = [{ value: "all", label: "All Networks" }];

    if (networkList) {
      const networkOpts = networkList?.result.map((network: any) => ({
        value: network.id.toString(),
        label: network.networkName,
      }));
      options.push(...networkOpts);
    }

    return options;
  }, [networkList]);

  // Create form fields for the filters
  const filterFields: FormField[] = [
    {
      name: "facility",
      label: "Facility",
      type: "select",
      placeholder: "Select Facility",
      options: facilityOptions,
      defaultValue: filters.facility || "all",
      classes: {
        container: "w-full",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
      },
    },
    {
      name: "system",
      label: "System",
      type: "select",
      placeholder: "Select System",
      options: systemOptions,
      defaultValue: filters.system || "all",
      classes: {
        container: "w-full",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
      },
    },
    {
      name: "network",
      label: "Network",
      type: "select",
      placeholder: "Select Network",
      options: networkOptions,
      defaultValue: filters.network || "all",
      classes: {
        container: "w-full",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select:
          "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
      },
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    defaultValues: filters,
  });

  // Handle field changes and propagate to parent
  const handleFieldChange = (fieldName: string, value: any) => {
    // Call the parent's filter change handler
    onFilterChange(fieldName, value);
  };

  // Handle form submission (Apply Filters) - optional if you want an apply button
  const handleApplyFilters = (formData: any) => {
    Object.keys(formData).forEach((key) => {
      onFilterChange(key, formData[key]);
    });
  };

  // Sync form with external filter changes
  useEffect(() => {
    // Update form values when filters prop changes
    Object.keys(filters).forEach((key) => {
      const currentValue = form.getValues(key);
      const newValue = filters[key as keyof typeof filters];

      if (currentValue !== newValue) {
        form.setValue(key, newValue);
      }
    });
  }, [filters, form]);

  // Reset form when filters are cleared externally
  useEffect(() => {
    const allFiltersAreDefault = Object.values(filters).every(
      (value) => !value || value === "all"
    );

    if (allFiltersAreDefault) {
      form.reset({
        facility: "all",
        system: "all",
        network: "all",
      });
    }
  }, [filters, form]);

  return (
    <div>
      <div className="mb-4">
        <DynamicForm
          fields={filterFields}
          form={form}
          onSubmit={handleApplyFilters}
          gridColumns={3}
          responsiveColumns={{
            sm: 1,
            md: 2,
            lg: 3,
          }}
          config={{
            layout: "grid",
            showSubmitButton: false, // We handle filtering on change
            showResetButton: false, // We have custom clear button
            spacing: "medium",
          }}
          className="mb-4"
        />
      </div>

      {/* Filter Actions */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          {hasActiveFilters ? "Filters applied" : "No filters applied"}
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
            disabled={!hasActiveFilters}
            className="flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Clear Filters
          </Button>

          <Button
            variant="default"
            size="sm"
            onClick={() => handleApplyFilters(form.getValues())}
            className="flex items-center gap-2"
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;
